import type {PropertyDefinitionBase, PropertySourceDetails, PropertyValue} from '@github-ui/custom-properties-types'
import {isEmptyPropertyValue} from '@github-ui/custom-properties-types/helpers'
import {enterprisePath, ownerPath} from '@github-ui/paths'
import {KebabHorizontalIcon, ShieldLockIcon, SyncIcon, UndoIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, FormControl, IconButton, Link, Token, Truncate} from '@primer/react'
import {type ReactNode, useMemo} from 'react'

import type {PropertyField} from '../hooks/use-edit-custom-properties'
import {CustomPropertyInput} from './CustomPropertyInput'
import styles from './CustomPropertyValuesTable.module.css'

interface ReadTableProps {
  definitions: PropertyDefinitionBase[]
  propertyValuesMap: Record<string, PropertyField>
  showUndo?: boolean
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
}

export interface EditTableProps extends ReadTableProps {
  editableDefinitions: PropertyDefinitionBase[]
  setPropertyValue: (propertyName: string, value: PropertyValue) => void
  revertPropertyValue: (propertyName: string) => void
  fetchAllowedValuesUrl?: (propertyName: string) => string
}

type CustomPropertyValuesTableProps = ReadTableProps | EditTableProps

export function CustomPropertyValuesTable(props: CustomPropertyValuesTableProps) {
  const {definitions, propertyValuesMap, showUndo = true, managedBySource} = props

  const {editableDefinitions, fetchAllowedValuesUrl} = props as EditTableProps
  const editableDefinitionsSet = useMemo(
    () => new Set((editableDefinitions || []).map((definition: PropertyDefinitionBase) => definition.propertyName)),
    [editableDefinitions],
  )

  if (definitions.length === 0) {
    return null
  }

  return (
    <div data-testid="custom-property-values-table" className={styles.tableContainer}>
      {definitions.map(definition => {
        const {propertyName} = definition
        const field = propertyValuesMap[propertyName]
        const isEditable = 'editableDefinitions' in props && editableDefinitionsSet.has(propertyName)

        return (
          <div data-testid="property-row" key={propertyName} className={styles.propertyRow}>
            {isEditable ? (
              <CustomPropertyEditValueRow
                key={propertyName}
                changed={!!field?.changed}
                mixed={!!field?.mixed}
                propertyValue={field?.value}
                definition={definition}
                error={field?.error}
                onChange={newValue => props.setPropertyValue(propertyName, newValue)}
                onReset={() => props.revertPropertyValue(propertyName)}
                showUndo={showUndo}
                fetchAllowedValuesUrl={fetchAllowedValuesUrl}
              />
            ) : (
              <CustomPropertyReadValueRow
                key={propertyName}
                propertyValue={field?.value || definition.defaultValue || ''}
                definition={definition}
                managedBySource={managedBySource}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

function CustomPropertyReadValueRow({
  propertyValue,
  definition,
  managedBySource,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinitionBase
  managedBySource?: Omit<PropertySourceDetails, 'avatarUrl'>
}) {
  const {description} = definition

  return (
    <DataTableRow
      label={getControlLabel(definition)}
      description={description}
      value={<DisplayPropertyValue propertyValue={propertyValue} definition={definition} />}
      bottomMessage={
        managedBySource && (
          <div className={styles.inlineContainer}>
            <span className={styles.mutedText}>
              <ShieldLockIcon size={'small'} /> Managed by{' '}
            </span>
            <Link
              inline
              href={
                managedBySource.type === 'org'
                  ? ownerPath({owner: managedBySource.slug})
                  : enterprisePath({slug: managedBySource.slug})
              }
            >
              {managedBySource.name}
            </Link>
          </div>
        )
      }
    />
  )
}

function CustomPropertyEditValueRow({
  propertyValue,
  definition,
  changed,
  mixed = false,
  error,
  onChange,
  onReset,
  showUndo,
  fetchAllowedValuesUrl,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinitionBase
  changed: boolean
  mixed?: boolean
  error?: string
  onChange: (value: PropertyValue) => void
  onReset(): void
  showUndo: boolean
  fetchAllowedValuesUrl?: (propertyName: string) => string
}) {
  const {propertyName, description} = definition
  const propertyNameValidationId = `${propertyName}-validation-error`

  return (
    <DataTableRow
      label={getControlLabel(definition)}
      description={description}
      value={
        <>
          <div className={styles.controlsContainer}>
            <CustomPropertyInput
              {...definition}
              propertyValue={propertyValue}
              mixed={mixed}
              onChange={onChange}
              inputProps={error ? {'aria-describedby': propertyNameValidationId} : {}}
              fetchAllowedValuesUrl={fetchAllowedValuesUrl}
            />
          </div>
          {error && (
            <FormControl.Validation
              variant="error"
              id={propertyNameValidationId}
              className={styles.FormControl_Validation}
            >
              {error}
            </FormControl.Validation>
          )}
        </>
      }
      trailing={<ResetDropdown definition={definition} {...{changed, onReset, onChange, showUndo}} />}
    />
  )
}

function DataTableRow({
  label,
  description,
  bottomMessage,
  value,
  trailing,
}: {
  label: string
  description: string | null
  bottomMessage?: ReactNode
  value: ReactNode
  trailing?: ReactNode
}) {
  return (
    <FormControl className={styles.FormControl}>
      <FormControl.Label data-testid="property-name" className={styles.FormControl_Label}>
        {label}
      </FormControl.Label>
      <div className={styles.formInputArea}>{value}</div>
      {trailing ? <div className={styles.formTrailingArea}>{trailing}</div> : null}
      {description && <FormControl.Caption className={styles.FormControl_Caption}>{description}</FormControl.Caption>}
      {bottomMessage ? <div className={styles.formBottomMessageArea}>{bottomMessage}</div> : null}
    </FormControl>
  )
}

function DisplayPropertyValue({
  propertyValue,
  definition,
}: {
  propertyValue?: PropertyValue
  definition: PropertyDefinitionBase
}) {
  if (isEmptyPropertyValue(propertyValue)) {
    return <span className={styles.mutedText}>(Empty)</span>
  }

  switch (definition.valueType) {
    case 'single_select':
    case 'true_false':
      return (
        <Token
          text={
            <Truncate title={propertyValue as string} maxWidth="100%">
              {propertyValue}
            </Truncate>
          }
          className={styles.propertyToken}
        />
      )
    case 'multi_select':
      return (
        <>
          {(propertyValue as string[]).map(v => (
            <Token
              key={v}
              text={
                <Truncate title={v} maxWidth="100%">
                  {v}
                </Truncate>
              }
              className={styles.spacedPropertyToken}
            />
          ))}
        </>
      )
    case 'string':
    case 'url':
      return (
        <Truncate title={propertyValue as string} maxWidth="100%">
          {propertyValue}
        </Truncate>
      )
  }
}

export function getControlLabel({propertyName, required}: PropertyDefinitionBase) {
  return `${propertyName}${required ? ' *' : ''}`
}

export function ResetDropdown({
  definition,
  changed,
  showUndo = true,
  onReset,
  onChange,
}: {
  definition: PropertyDefinitionBase
  changed: boolean
  showUndo?: boolean
  onReset(): void
  onChange: (value: string) => void
}) {
  const {propertyName, required, source} = definition
  const undoTitle = `Undo ${propertyName} changes`
  const resetToDefaultTitle = `Reset ${propertyName} to default value`

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          variant="invisible"
          aria-label={`Open reset options for ${propertyName}`}
          description="Reset options"
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="small">
        <ActionList>
          {showUndo && (
            <ActionList.Item
              onSelect={onReset}
              aria-label={undoTitle}
              inactiveText={changed ? undefined : 'No change to undo'}
            >
              <ActionList.LeadingVisual>
                <UndoIcon />
              </ActionList.LeadingVisual>
              <span className={styles.propertyLabel}>Undo</span>
              <span className={styles.propertyDescription}>Reset to the last used value</span>
            </ActionList.Item>
          )}
          {required && (
            <ActionList.Item onSelect={() => onChange('')} aria-label={resetToDefaultTitle}>
              <ActionList.LeadingVisual>
                <SyncIcon />
              </ActionList.LeadingVisual>
              <span className={styles.propertyLabel}>Reset to default</span>
              <span className={styles.propertyDescription}>
                Inherit the value defined by the {source.type === 'business' ? 'enterprise' : 'organization'}
              </span>
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ CustomPropertyValuesTable.displayName ||= 'CustomPropertyValuesTable' } catch {}
try{ CustomPropertyReadValueRow.displayName ||= 'CustomPropertyReadValueRow' } catch {}
try{ CustomPropertyEditValueRow.displayName ||= 'CustomPropertyEditValueRow' } catch {}
try{ DataTableRow.displayName ||= 'DataTableRow' } catch {}
try{ DisplayPropertyValue.displayName ||= 'DisplayPropertyValue' } catch {}
try{ ResetDropdown.displayName ||= 'ResetDropdown' } catch {}